/**
 * Ponovno povezovanje z eksponentnim zamikom (D1, faza 1).
 *
 * Čista funkcija z vbrizganim virom naključja, ker je to eno od dveh mest,
 * kjer T1 §12 pričakuje največ pravih napak (drugo je razčlenjevanje ICY).
 * Adapter samo odmeri čas — pravilo je tu in je testirano.
 */

export interface BackoffPolicy {
  /** Zamik pred prvim ponovnim poskusom. */
  baseMs: number;
  factor: number;
  /** Zgornja meja: brez nje bi šesti poskus čakal minute. */
  maxMs: number;
  /** Koliko ponovnih poskusov, preden odnehamo. */
  maxAttempts: number;
  /** Delež zamika, za katerega sme tresek odstopati (0–1). */
  jitter: number;
}

/**
 * 1 s → 2 s → 4 s → 8 s → 16 s → 30 s (odrezano), nato odnehamo.
 *
 * Tresek ni kozmetika: ob izpadu postaje se vsi poslušalci prekinejo hkrati
 * in bi se brez njega hkrati tudi vrnili — kar postajo, ki se ravno pobira,
 * spet podre.
 */
export const STREAM_BACKOFF: BackoffPolicy = {
  baseMs: 1_000,
  factor: 2,
  maxMs: 30_000,
  maxAttempts: 6,
  jitter: 0.25,
};

/** `attempt` je 1-osnovan: 1 je prvi ponovni poskus. */
export function shouldRetry(attempt: number, policy: BackoffPolicy = STREAM_BACKOFF): boolean {
  return attempt >= 1 && attempt <= policy.maxAttempts;
}

/**
 * Zamik pred `attempt`-im ponovnim poskusom, v milisekundah.
 *
 * Tresek je simetričen okoli izračunanega zamika, zato zamik nikoli ne
 * preseže `maxMs` za več kot `jitter` in nikoli ne pade pod 0.
 */
export function backoffDelay(
  attempt: number,
  policy: BackoffPolicy = STREAM_BACKOFF,
  random: () => number = Math.random,
): number {
  const step = Math.max(1, Math.trunc(attempt));
  const capped = Math.min(policy.baseMs * policy.factor ** (step - 1), policy.maxMs);
  const delta = (random() * 2 - 1) * capped * policy.jitter;
  return Math.max(0, Math.round(capped + delta));
}
